/**
 * This file defines utility functions for working with icons, including a Vue plugin
 * for registering the icons with the Vue application.
 */

import type { App } from 'vue';
import { iconsMap } from '@/icons.ts';

type IconDefinition = {
    name: string;
    width: number;
    height: number;
    viewBox: string;
    data: string;
};

type IconMap = Record<string, IconDefinition>;

// Friendly shorthand names for the popular icons. These will be available globally via the $icons object.
const icons: Record<string, string> = {
    account: 'regular/shop',
    add: 'bold/add',
    advisor: 'regular/accessories-tie',
    appointment: 'regular/calendar-3',
    attempted: 'regular/password-desktop-approved',
    attribution: 'regular/hyperlink-circle-horizontal',
    bdc: 'regular/headphones-customer-support',
    beBack: 'regular/data-transfer-three-back-forth-back',
    calculator: 'regular/accounting-calculator',
    camera: 'regular/meeting-camera',
    checkCircle: 'bold/check-circle-1',
    collapse: 'regular/arrow-up-1',
    commercialDirector: 'regular/cash-briefcase',
    csi: 'regular/like-1',
    delivery: 'regular/car-1',
    deposit: 'regular/cash-payment-bag',
    duplicate: 'regular/multiple-neutral-2',
    email: 'regular/email-action-unread',
    emailVideo: 'regular/drawer-play',
    emailBold: 'bold/email-action-unread',
    event: 'regular/megaphone-1',
    excluded: 'regular/alert-diamond',
    expand: 'regular/arrow-down-1',
    form: 'regular/common-file-text',
    homeRoadTest: 'regular/house-chimney-2',
    homeVisit: 'regular/house-chimney-2',
    otherVisit: 'regular/maps',
    inTurn: 'regular/button-shuffle',
    inStock: 'regular/login-keys',
    leadXpress: 'regular/earth-1',
    leadLost: 'regular/single-neutral-actions-block',
    loading: 'regular/button-refresh-arrows',
    lost: 'regular/car-actions-disable-1',
    loyalty: 'regular/award-ribbon-star-3',
    meetingOff: 'regular/single-neutral-actions-block',
    meetingOn: 'regular/single-neutral-actions-check-2',
    messenger: 'regular/social-media-facebook-1',
    navigationDotsVertical: 'bold/navigation-menu-vertical',
    navigationDotsHorizontal: 'bold/navigation-menu-hotizontal',
    openExternalLink: 'regular/open-external-link',
    pencilWrite: 'regular/pencil-write',
    phone: 'regular/phone',
    phoneAppointment: 'regular/phone-landline',
    phoneBold: 'bold/phone',
    reached: 'regular/single-neutral-actions-check-2',
    renewal: 'regular/transportation-ticket-car-transfer-1',
    roadTest: 'regular/road-straight',
    sale: 'regular/money-bag-dollar',
    service: 'regular/wench-1',
    source: 'regular/earth-1',
    singleNeutralRemove: 'regular/single-neutral-actions-remove',
    sms: 'regular/messages-bubble-typing-1',
    smsBold: 'bold/messages-bubble-typing-1',
    takeOver: 'regular/police-man-1',
    testDrive: 'regular/road-woods',
    time: 'regular/stopwatch',
    timeClockCircle: 'regular/time-clock-circle',
    trash: 'regular/trash-1',
    upSale: 'regular/arrow-thick-up',
    virtualAppointment: 'regular/meeting-camera',
    visit: 'regular/walking',
    visitWidget: 'bold/information-desk-customer',
    warning: 'regular/alert-triangle',
    webBoost: 'regular/stopwatch',
    webOrder: 'regular/shopping-cart-check',
};

// Returns the icon object from the iconsMap.
export function getIcon(name: string): IconDefinition | undefined {
    return (iconsMap as IconMap)[name];
}

export interface IconMarkupOptions {
    class?: string;
    width?: string;
    height?: string;
}

// Returns the SVG markup for the icon.
export function getIconMarkup(name: string, options: IconMarkupOptions = {}): string {
    const icon = getIcon(name);
    if (!icon) {
        return '';
    }

    const classes = ['svg-icon'];
    let width = '1em';
    let height = '1em';

    if (name.includes('bold/')) {
        classes.push('svg-fill');
    }

    if (options.class) {
        classes.push(options.class);
    }

    if (options.width) {
        width = options.width;
    }

    if (options.height) {
        height = options.height;
    }

    return `
        <svg
            version="1.1"
            class="${classes.join(' ')}"
            viewBox="${icon.viewBox}"
            style="width: ${width}; height: ${height};"
        >
            ${icon.data}
        </svg>
    `;
}

export const iconsPlugin = {
    install(app: App) {
        if (!app || !app.config || !app.config.globalProperties) {
            return;
        }

        const globalProperties = app.config.globalProperties as Record<string, unknown>;
        globalProperties.$icons = icons;
        app.provide('icons', icons);
    },
};

export default icons;
